import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../../context/MaestroContext';
import maestroService from '../../maestroService';
import { Delegacao } from '../../models/delegacao';
import { dispararAcaoSimulada } from '../../utils/acaoSimulada';
import { formatarData } from '../../utils/formatters';
import { COR_STATUS_DELEGACAO, LABEL_STATUS_DELEGACAO } from '../../utils/labels';
import Card from '../Card';
import EstadoVazio from '../EstadoVazio';

const ListaDelegacoes: React.FC<{
  titulo: string;
  delegacoes: Delegacao[];
  permiteRevogar: boolean;
  onRevogar?: (d: Delegacao) => void;
}> = ({ titulo, delegacoes, permiteRevogar, onRevogar }) => {
  const { getUsuario, empresasEscopo } = useMaestroContext();

  if (delegacoes.length === 0) {
    return <EstadoVazio icone='bi bi-arrow-left-right' titulo={`Nenhuma delegação ${titulo.toLowerCase()}`} />;
  }

  return (
    <div className='mc-membros-lista'>
      {delegacoes.map(d => {
        const empresa = empresasEscopo.find(e => e.id === d.empresaId);
        const de = getUsuario(d.delegadoDeUsuarioId);
        const para = getUsuario(d.delegadoParaUsuarioId);
        return (
          <div key={d.id} className='mc-membro-card' style={{ opacity: d.ehVigente ? 1 : .6 }}>
            <div className='mc-membro-card__nome'>{de?.nome ?? `Usuário #${d.delegadoDeUsuarioId}`} → {para?.nome ?? `Usuário #${d.delegadoParaUsuarioId}`}</div>
            <div className='mc-membro-card__email'>{empresa?.nome ?? `Empresa #${d.empresaId}`} · {formatarData(d.dataInicio)} a {formatarData(d.dataFim)} · {d.motivo}</div>
            <span className={`mc-badge mc-badge--${COR_STATUS_DELEGACAO[d.status]}`}>{LABEL_STATUS_DELEGACAO[d.status]}{d.ehVigente ? ' · vigente agora' : ''}</span>
            {permiteRevogar && d.status === 'ATIVA' && onRevogar && (
              <button type='button' className='mc-btn mc-btn--sm mc-btn--secondary' onClick={() => onRevogar(d)}>Revogar</button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const VisaoDelegacao: React.FC = () => {
  const { usuarios, empresasEscopo } = useMaestroContext();
  const [concedidas, setConcedidas] = useState<Delegacao[]>([]);
  const [recebidas, setRecebidas] = useState<Delegacao[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [criando, setCriando] = useState(false);
  const [paraUsuarioId, setParaUsuarioId] = useState(0);
  const [empresaId, setEmpresaId] = useState(0);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const carregar = () => {
    setCarregando(true);
    Promise.all([maestroService.listarDelegacoesConcedidas(), maestroService.listarDelegacoesRecebidas()])
      .then(([c, r]) => { setConcedidas(c); setRecebidas(r); })
      .catch(() => { setConcedidas([]); setRecebidas([]); })
      .finally(() => setCarregando(false));
  };

  useEffect(carregar, []);

  const criarDelegacao = () => {
    if (!paraUsuarioId || !empresaId || !dataInicio || !dataFim || !motivo.trim()) return;
    setEnviando(true);
    maestroService.criarDelegacao({ delegadoParaUsuarioId: paraUsuarioId, empresaId, dataInicio, dataFim, motivo: motivo.trim() })
      .then(criada => {
        setConcedidas(prev => [criada, ...prev]);
        setCriando(false); setParaUsuarioId(0); setEmpresaId(0); setDataInicio(''); setDataFim(''); setMotivo('');
      })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar a delegação.'))
      .finally(() => setEnviando(false));
  };

  const revogar = (d: Delegacao) => {
    if (!window.confirm('Revogar esta delegação agora? O acesso delegado é retirado imediatamente.')) return;
    maestroService.revogarDelegacao(d.id)
      .then(atualizada => setConcedidas(prev => prev.map(x => x.id === atualizada.id ? atualizada : x)))
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível revogar a delegação.'));
  };

  if (carregando) return <p className='mc-texto-neutro'>Carregando delegações...</p>;

  return (
    <>
      <Card title='Delegar responsabilidade' subtitle='Transfere temporariamente sua atuação numa empresa para outro usuário do Maestro. Expira sozinha na data-fim (job automático) ou pode ser revogada antes.'>
        {!criando ? (
          <button type='button' className='mc-btn mc-btn--secondary' onClick={() => { setEmpresaId(empresasEscopo[0]?.id ?? 0); setCriando(true); }}>
            <i className='bi bi-plus-lg' /> Nova delegação
          </button>
        ) : (
          <div className='mc-form-inline'>
            <label>Delegar para
              <select value={paraUsuarioId} onChange={e => setParaUsuarioId(Number(e.target.value))}>
                <option value={0}>Selecione...</option>
                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </label>
            <label>Empresa
              <select value={empresaId} onChange={e => setEmpresaId(Number(e.target.value))}>
                <option value={0}>Selecione...</option>
                {empresasEscopo.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </label>
            <label>Início<input type='date' value={dataInicio} onChange={e => setDataInicio(e.target.value)} /></label>
            <label>Fim<input type='date' value={dataFim} onChange={e => setDataFim(e.target.value)} /></label>
            <label>Motivo<input type='text' placeholder='Ex.: Férias, licença' value={motivo} onChange={e => setMotivo(e.target.value)} /></label>
            <button type='button' className='mc-btn mc-btn--primary' disabled={enviando || !paraUsuarioId || !empresaId || !dataInicio || !dataFim || !motivo.trim()} onClick={criarDelegacao}>
              {enviando ? 'Criando...' : 'Delegar'}
            </button>
            <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setCriando(false)}>Cancelar</button>
          </div>
        )}
      </Card>

      <Card title='Concedidas por mim' subtitle='Delegações que você criou.'>
        <ListaDelegacoes titulo='concedida' delegacoes={concedidas} permiteRevogar onRevogar={revogar} />
      </Card>

      <Card title='Recebidas' subtitle='Delegações que outros usuários fizeram para você.'>
        <ListaDelegacoes titulo='recebida' delegacoes={recebidas} permiteRevogar={false} />
      </Card>
    </>
  );
};

export default VisaoDelegacao;
